import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Container, Skeleton } from "@mui/material";
import { motion, useScroll, useTransform } from "framer-motion";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { FONT_FAMILY_SERIF } from "../../theme";
import { getResponsiveImageAttributes } from "../../utils/imageUtils";

interface HeroProps {
  imageUrl?: string;
  title?: string;
  subtitle?: string;
  ctaText?: string;
  onCtaClick?: () => void;
}

export const Hero: React.FC<HeroProps> = ({
  imageUrl,
  title,
  subtitle,
  ctaText,
  onCtaClick,
}) => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]); // Parallax effect
  const [imageLoaded, setImageLoaded] = useState(false);

  // Reset loaded state when image URL changes
  useEffect(() => {
    setImageLoaded(false);
  }, [imageUrl]);

  return (
    <Box
      sx={{
        position: "relative",
        height: { xs: "calc(60vh - 80px)", md: "calc(100vh - 80px)" }, // Account for header height
        minHeight: { xs: "400px", md: "600px" }, // Ensure minimum height
        width: "100%",
        overflow: "hidden", // Prevent parallax from extending beyond bounds
      }}
    >
      {/* Skeleton loader */}
      {!imageLoaded && imageUrl && (
        <Skeleton
          variant="rectangular"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            width: "100%",
            height: "100%",
          }}
        />
      )}

      {/* Background Image with Parallax */}
      {imageUrl && (
        <motion.div
          style={{
            y,
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: imageLoaded ? 0 : -1,
            overflow: "hidden", // Prevent image from extending beyond bounds
          }}
        >
          <Box
            component="img"
            key={imageUrl} // Force reload when URL changes
            alt="Mariah Blue Photography - Professional photography portfolio"
            {...(imageUrl && imageUrl.includes("cloudinary.com")
              ? getResponsiveImageAttributes(imageUrl, {
                  widths: [800, 1200, 1600, 2000],
                  sizes: "100vw",
                  defaultSize: 1600,
                })
              : { src: imageUrl })}
            loading="eager"
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageLoaded(true)} // Hide skeleton even on error
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
            }}
          />
        </motion.div>
      )}


      {/* Content - Only show if title or subtitle is provided */}
      {(title || subtitle || ctaText) && (
        <Container
          maxWidth="lg"
          sx={{
            position: "relative",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            zIndex: 2,
            py: { xs: 4, md: 6 }, // Add vertical padding to ensure content isn't cut off
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Box
              sx={{
                backgroundColor: (theme) => theme.custom.overlays.dark,
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                borderRadius: "12px",
                padding: { xs: 4, md: 6 },
                maxWidth: "800px",
              }}
            >
              {title && (
                <Typography
                  variant="h1"
                  sx={{
                    fontFamily: FONT_FAMILY_SERIF,
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" }, // 32px, 40px, 48px
                    fontWeight: 300,
                    color: "white",
                    mb: 2,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {title}
                </Typography>
              )}

              {subtitle && (
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: "0.875rem", md: "1rem" }, // 14px, 16px
                    color: (theme) => theme.custom.overlays.whiteStrong,
                    mb: 4,
                    lineHeight: 1.7,
                  }}
                >
                  {subtitle}
                </Typography>
              )}

              {ctaText && (
                <Button
                  variant="text"
                  endIcon={<ArrowForwardIcon />}
                  onClick={onCtaClick}
                  disableRipple
                  sx={{
                    color: "white",
                    border: "none",
                    boxShadow: "none",
                    padding: { xs: "14px 24px", sm: "12px 32px" }, // Better touch target on mobile
                    fontSize: { xs: "16px", sm: "0.875rem" }, // Prevent zoom on iOS
                    fontWeight: 500,
                    letterSpacing: "0.02em",
                    minHeight: { xs: "44px", sm: "auto" }, // Better touch target
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: (theme) =>
                        theme.custom.overlays.whiteLight,
                      border: "none",
                      boxShadow: "none",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  {ctaText}
                </Button>
              )}
            </Box>
          </motion.div>
        </Container>
      )}
    </Box>
  );
};
